import React from "react";
import {
  FileText,
  Upload,
  Edit2,
  X,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import Button from "./common/ui/button/Button";
import Label from "./common/form/Label";
import Input from "./common/form/input/InputField";
import { Modal } from "./common/ui/modal";
import { Card } from "./common/Card";
import { PdfSignatureBoxDrawer } from "./PdfSignatureBoxDrawer";

import { usePdfManagement } from "../contexts/PdfManagementContext";
import { useAlert } from "../hooks/useAlert";

/**
 * PDF Template Manager Component
 * Handles UI for uploading, managing, and configuring PDF templates with signature fields
 */
export const PdfTemplateManager: React.FC = () => {
  const {
    pdfDocuments,
    currentDocumentIndex,
    setCurrentDocumentIndex,
    showSignatureModal,
    setShowSignatureModal,
    handlePdfFilesChange,
    handleRemoveDocument,
    handleUpdateDocumentTitle,
    handleMoveDocumentUp,
    handleMoveDocumentDown,
  } = usePdfManagement();

  const { setAlert } = useAlert();

  return (
    <>
      {/* PDF Template Upload Section */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <div>
            <Label>PDF Templates for Signatures (Optional)</Label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Upload up to 3 PDF documents to enable signature requests. Signers
              will sign all documents in one session.
            </p>
          </div>
          {pdfDocuments.length < 5 && (
            <div className="relative">
              <input
                type="file"
                accept="application/pdf"
                onChange={handlePdfFilesChange}
                className="hidden"
                id="pdf-upload"
                multiple
              />
              <Button
                type="button"
                size="sm"
                variant="primary"
                onClick={() => document.getElementById("pdf-upload")?.click()}
                startIcon={<Upload size={16} />}
              >
                Upload PDFs
              </Button>
            </div>
          )}
        </div>

        {pdfDocuments.length === 0 ? (
          <div className="relative">
            <input
              type="file"
              accept="application/pdf"
              onChange={handlePdfFilesChange}
              className="hidden"
              id="pdf-upload-empty"
              multiple
            />
            <label
              htmlFor="pdf-upload-empty"
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900/40 dark:hover:bg-gray-900/60"
            >
              <Upload size={48} className="mb-4 text-gray-400" />
              <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Click to upload PDF templates
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                PDF files only, max 10MB each, up to 5 documents
              </p>
            </label>
          </div>
        ) : (
          <div className="space-y-3">
            {pdfDocuments.map((doc, index) => (
              <Card key={doc.id ?? index}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col gap-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleMoveDocumentUp(index)}
                        disabled={index === 0}
                      >
                        <ChevronUp size={16} />
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleMoveDocumentDown(index)}
                        disabled={index === pdfDocuments.length - 1}
                      >
                        <ChevronDown size={16} />
                      </Button>
                    </div>
                    <div className="rounded-lg bg-red-50 p-2 dark:bg-red-900/20">
                      <FileText
                        size={24}
                        className="text-red-600 dark:text-red-400"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        type="text"
                        value={doc.title}
                        onChange={(e) =>
                          handleUpdateDocumentTitle(index, e.target.value)
                        }
                        placeholder="Document title"
                        className="mb-1"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {doc.signaturePlaces.length} signature field
                        {doc.signaturePlaces.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setCurrentDocumentIndex(index);
                        setShowSignatureModal(true);
                      }}
                      startIcon={<Edit2 size={16} />}
                    >
                      Configure
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleRemoveDocument(index)}
                      startIcon={<X size={16} />}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* PDF Signature Configuration Modal (Full Screen) */}
      {pdfDocuments.length > 0 && pdfDocuments[currentDocumentIndex] && (
        <Modal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          title={`Configure Signature Fields - ${pdfDocuments[currentDocumentIndex].title}`}
          isFullscreen
        >
          <PdfSignatureBoxDrawer />
          <div className="mt-4 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowSignatureModal(false)}
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={() => {
                const currentDoc = pdfDocuments[currentDocumentIndex];
                if (currentDoc.signaturePlaces.length === 0) {
                  setAlert({
                    variant: "error",
                    title: "No Signature Fields",
                    message:
                      "Please add at least one signature field before closing",
                  });
                  return;
                }

                setShowSignatureModal(false);
              }}
            >
              {`Done (${
                pdfDocuments[currentDocumentIndex].signaturePlaces.length
              } field${
                pdfDocuments[currentDocumentIndex].signaturePlaces.length !== 1
                  ? "s"
                  : ""
              })`}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};
